import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { createDashboardTheme } from './styles/dashboard-theme.js';
import apiClient from '../../api/client.js';

// Context Providers
import DashboardProvider from './contexts/DashboardProvider';
import DashboardContext from './contexts/DashboardContext';

// Layout Components
import MainLayout from './layout/MainLayout';

// Import all your page components
import Overview from './pages/Overview';
import AddCategory from './pages/category/AddCategory.jsx';
import CategoryList from './pages/category/CategoryList';
import AddMenu from './pages/menu/AddMenu';
import MenuList from './pages/menu/MenuList';
import PosScreen from './pos/PosScreen';
import OrderList from './pages/OrderList.jsx';
import SalesReport from './pages/SalesReport.jsx';
import Ingredients from "./pages/ingredients/Ingredients";
// import Ingredients from "./pages/ingredients/IngredientsManagement";
// import IngredientsPurchases from "./pages/ingredients/IngredientPurchases";
import StockSetup from './pages/inventory/setup/StockSetup';
import StockCount from './pages/inventory/stockcount/StockCount';
import ProductionLog from './pages/inventory/productionlog/ProductionLog.jsx';
import WastageLog from './pages/inventory/wastagelog/WastageLog.jsx';
import AiInsights from './pages/AiInsight.jsx';
import VendorSetting from './pages/management/VendorSetting.jsx';
import UserManagement from './pages/management/UserManagement.jsx';
import Gallery from './pages/management/Gallery.jsx'; // Gallery component
import Resource from './pages/support/Resource.jsx';
import ProfileSettings from './pages/profilesetting/ProfileSettings.jsx';
import Settings from './pages/header/settingspage/Settings.jsx';
import NotificationCenter from './pages/notifications/NotificationCenter.jsx';
import NotificationSettings from './pages/notifications/NotificationSettings.jsx';
import POSConfig from './pages/posconfig/POSConfig.jsx'; // ✅ NEW: POS Config
import UserSessionLog from './pages/management/UserSessionLog.jsx'; // ✅ NEW: User Activity Log


// POS Route Component
const POSRoute = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/dashboard/pos-fullscreen');
  }, [navigate]);

  return null;
};

// Standalone POS Screen
const StandalonePOS = () => {
  return (
    <div className="pos-screen-container">
      <PosScreen />
    </div>
  );
};

// Permission URL mapping
const PERMISSION_URL_MAP = {
  'home': '/dashboard', 
  'add-category': '/dashboard/category/add',
  'category-list': '/dashboard/category/list',
  'add-menu': '/dashboard/menu/add',
  'menu-list': '/dashboard/menu/list',
  'order-list': '/dashboard/orders',
  'sales-report': '/dashboard/sales',
  'inventory-ingredients': '/dashboard/inventory/ingredients',
  'inventory-ingredients-purchases': '/dashboard/inventory/ingredients/purchases',
  'inventory-stock': '/dashboard/inventory/stock',
  'inventory-production': '/dashboard/inventory/production', 
  'inventory-wastage': '/dashboard/inventory/wastage',
  'ai-insight': '/dashboard/ai-insight',
  'vendor-settings': '/dashboard/vendor-settings',
  'user-list': '/dashboard/users',
  'activity-log': '/dashboard/activity-log', // ✅ NEW: Activity Log permission
  'pos-config': '/dashboard/pos-config', // ✅ NEW: POS Config permission
  'notifications': '/dashboard/notifications',
  'notification-settings': '/dashboard/notifications/settings',
  'resources': '/dashboard/resources',
  'profile': '/dashboard/profile',
  'pos-system': '/dashboard/pos',
  'settings': '/dashboard/settings'
};

// Component that provides theme based on context
const ThemedApp = ({ children }) => {
  const { isDarkMode } = useContext(DashboardContext);
  const dynamicTheme = useMemo(() => 
    createDashboardTheme(isDarkMode ? 'dark' : 'light'), 
    [isDarkMode]
  );
  
  return (
    <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  
  // Memoize initial token to prevent re-renders
  const initialToken = useMemo(() => localStorage.getItem('token'), []);
  
  // State management
  const [dashboardData, setDashboardData] = useState({
    userPermissions: [],
    vendorData: null,
    dashboardStats: null,
    userRole: null,
    isLoading: true
  });

  // ✅ FIXED: Simplified permission check function with proper debugging
  const isRouteAccessible = useCallback((routePath) => {
    console.log('🔍 Dashboard: Checking route access:', {
      routePath,
      userRole: dashboardData.userRole,
      isLoading: dashboardData.isLoading
    });
    
    // ✅ CRITICAL FIX: Always allow basic pages
    if (routePath === 'profile' || 
        routePath === 'home' || 
        routePath === 'pos-system') {
      console.log('✅ Dashboard: Basic page access granted');
      return true;
    }
    
    // Admin has access to everything
    if (dashboardData.userRole === 'Admin') {
      console.log('✅ Dashboard: Admin access granted');
      return true;
    }
    
    // ✅ FIXED: Manager permissions
    if (dashboardData.userRole === 'Manager') {
      // Manager can access POS Config and Notifications
      if (routePath === 'pos-config' || 
          routePath === 'notifications' ||
          routePath === 'notification-settings' ||
          routePath === 'activity-log') {
        console.log('✅ Dashboard: Manager config/notification access granted');
        return true;
      }
      
      // Manager CANNOT access user management and vendor settings
      if (routePath === 'user-list' || routePath === 'vendor-settings') {
        console.log('❌ Dashboard: Manager management access denied');
        return false;
      }
      
      // ✅ CRITICAL FIX: Allow other pages for Manager
      console.log('✅ Dashboard: Manager general access granted');
      return true;
    }
    
    // ✅ FIXED: Staff permissions
    if (dashboardData.userRole === 'Staff') {
      // Staff CANNOT access management features or config
      if (routePath === 'user-list' || 
          routePath === 'vendor-settings' || 
          routePath === 'pos-config' ||
          routePath === 'notifications' ||
          routePath === 'notification-settings' ||
          routePath === 'activity-log') {
        console.log('❌ Dashboard: Staff restricted access denied');
        return false;
      }
      
      // ✅ CRITICAL FIX: Allow other pages for Staff
      console.log('✅ Dashboard: Staff general access granted');
      return true;
    }
    
    // ✅ FALLBACK: Check permissions array
    const permissionUrl = PERMISSION_URL_MAP[routePath];
    const hasSpecificPermission = permissionUrl ? 
      dashboardData.userPermissions.some(p => p.url === permissionUrl) : 
      false;
    
    console.log(hasSpecificPermission ? 
      '✅ Dashboard: Specific permission granted' : 
      '❌ Dashboard: No specific permission found'
    );
    
    return hasSpecificPermission;
  }, [dashboardData.userPermissions, dashboardData.userRole, dashboardData.isLoading]);

  // Memoize permission wrapper to prevent re-renders
  const withPermissionCheck = useCallback((path, element) => {
    if (dashboardData.isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      );
    }
    
    const hasAccess = isRouteAccessible(path);
    console.log(`🎯 Dashboard: Route ${path} access: ${hasAccess ? 'GRANTED' : 'DENIED'}`);
    
    return hasAccess ? element : <Navigate to="/dashboard" replace />;
  }, [dashboardData.isLoading, isRouteAccessible]);

  // Single effect for initial data loading
  useEffect(() => {
    // ✅ FIXED: Better condition check
    if (!initialToken) {
      navigate('/signin');
      return;
    }

    // ✅ FIXED: Only run if still loading (prevents duplicate calls)
    if (!dashboardData.isLoading) {
      return;
    }

    const fetchDashboardData = async () => {
      try {
        // Decode token to get user data
        const tokenData = JSON.parse(atob(initialToken.split('.')[1]));
        console.log('🔍 Dashboard: Token data loaded:', {
          role: tokenData.role,
          permissions: tokenData.permissions?.length || 0
        });
        
        // Set axios defaults
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
        
        // Fetch data in parallel
        const [vendorRes, orderSummaryRes] = await Promise.all([
          apiClient.get('/api/v1/users/vendor-details'),
          apiClient.get('/api/v1/report/dashboard-order-summary')
        ]);        
        
        // Store vendor details in localStorage
        const vendorDetails = vendorRes.data;
        localStorage.setItem('vendorDetails', JSON.stringify(vendorDetails));
        
        // Set permissions from token or default to empty array
        const permissions = Array.isArray(tokenData.permissions) ? tokenData.permissions : [];
        localStorage.setItem('userPermissions', JSON.stringify(permissions));
        
        // Update state once with all data
        setDashboardData({
          userPermissions: permissions,
          vendorData: vendorDetails,
          dashboardStats: orderSummaryRes.data,
          userRole: tokenData.role,
          isLoading: false
        });
        
        console.log('✅ Dashboard: Data loaded successfully:', {
          role: tokenData.role,
          permissionsCount: permissions.length
        });
        
      } catch (error) {
        console.error('❌ Dashboard: Error fetching dashboard data:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/signin');
        } else {
          setDashboardData(prev => ({ ...prev, isLoading: false }));
        }
      }
    };

    fetchDashboardData();
  }, [initialToken, dashboardData.isLoading, navigate]);


  // Memoize dashboard context value to prevent re-renders
  const dashboardContextValue = useMemo(() => ({
    vendorData: dashboardData.vendorData, 
    dashboardStats: dashboardData.dashboardStats,
    userRole: dashboardData.userRole,
    reloadPermissions: async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        localStorage.removeItem('userPermissions');
        const permissionsRes = await apiClient.get('/api/v1/auth/permissions');
        const permissions = Array.isArray(permissionsRes.data) ? permissionsRes.data : [];
        
        setDashboardData(prev => ({ ...prev, userPermissions: permissions }));
        localStorage.setItem('userPermissions', JSON.stringify(permissions));
        
        console.log('Permissions reloaded:', permissions);
      } catch (error) {
        console.error('Error reloading permissions:', error);
      }
    }
  }), [dashboardData.vendorData, dashboardData.dashboardStats, dashboardData.userRole]);

  if (dashboardData.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardProvider value={dashboardContextValue}>
      <ThemedApp>
        <Routes>
          <Route path="/" element={<MainLayout vendorData={dashboardData.vendorData} />}>
            <Route index element={withPermissionCheck('home', <Overview />)} />
           
            <Route path="category">
              <Route path="add" element={withPermissionCheck('add-category', <AddCategory />)} />
              <Route path="list" element={withPermissionCheck('category-list', <CategoryList />)} />
            </Route>
           
            <Route path="menu">
              <Route path="add" element={withPermissionCheck('add-menu', <AddMenu />)} />
              <Route path="list" element={withPermissionCheck('menu-list', <MenuList />)} />
            </Route>
           
            <Route path="orders" element={withPermissionCheck('order-list', <OrderList />)} />
           
            <Route path="sales" element={withPermissionCheck('sales-report', <SalesReport />)} />
           
            <Route path="ai-insight" element={withPermissionCheck('ai-insight', <AiInsights />)} />
           
            <Route path="vendor-settings" element={withPermissionCheck('vendor-settings', <VendorSetting />)} />
           
            <Route path="users" element={withPermissionCheck('user-list', <UserManagement />)} />
           
            {/* ✅ NEW: Activity Log Route */}
            <Route path="activity-log" element={withPermissionCheck('activity-log', <UserSessionLog />)} />
           
            {/* ✅ NEW: POS Config Route */}
            <Route path="pos-config" element={withPermissionCheck('pos-config', <POSConfig />)} />

            {/* Notification Routes */}
            <Route path="notifications">
              <Route index element={withPermissionCheck('notifications', <NotificationCenter />)} />
              <Route path="settings" element={withPermissionCheck('notification-settings', <NotificationSettings />)} />
            </Route>
           
            <Route path="resources" element={withPermissionCheck('resources', <Resource />)} />
           
            <Route path="profile" element={<ProfileSettings />} />
           
            <Route path="settings" element={<Settings />} />

            {/* Inventory Route */}
            <Route path="inventory">
              {/* <Route path="ingredients" element={withPermissionCheck('inventory-ingredients', <Ingredients />)} /> */}
              {/* <Route path="ingredients/purchases" element={withPermissionCheck('inventory-ingredients-purchases', <IngredientsPurchases />)} /> */}
              <Route path="ingredients" element={withPermissionCheck('inventory-ingredients', <Ingredients />)} />

              <Route path="setup" element={withPermissionCheck('inventory-stock', <StockSetup />)} />
              <Route path="stock" element={withPermissionCheck('inventory-stock', <StockCount />)} />
              <Route path="production" element={withPermissionCheck('inventory-production', <ProductionLog />)} />
              <Route path="wastage" element={withPermissionCheck('inventory-wastage', <WastageLog />)} />
            </Route>
          </Route>
          
          {/* POS Routes */}
          <Route path="pos" element={<POSRoute />} />
          <Route path="pos-fullscreen" element={withPermissionCheck('pos-system', <StandalonePOS />)} />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ThemedApp>
    </DashboardProvider>
  );
}

export default Dashboard;